import { motion } from 'framer-motion';
import { CheckCircle2, User, Building, ArrowRight, ShieldCheck } from 'lucide-react';

export default function ConfirmationScene({ onNext }) {
  return (
    <div data-testid="scene-confirmation" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-2xl"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-emerald-100 text-emerald-700 mb-4">
            <ShieldCheck size={14} /> Step 7
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
            Identity Confirmed
          </h2>
          <p className="mt-3 font-body text-lg text-[#737373]">
            User verification complete — ready to access the portal
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="bg-white rounded-2xl border border-black/5 shadow-2xl overflow-hidden"
        >
          {/* Success header */}
          <div className="bg-emerald-500 px-8 py-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
            >
              <CheckCircle2 size={48} className="text-white mx-auto mb-3" />
            </motion.div>
            <h3 className="font-heading font-bold text-xl text-white">Welcome to Shafafiya</h3>
            <p className="font-body text-sm text-white/80 mt-1">Your account has been activated successfully</p>
          </div>

          {/* User details */}
          <div className="p-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-4 p-4 rounded-xl bg-[#FAFAFA]">
                <div className="w-12 h-12 rounded-xl bg-teal-600/10 flex items-center justify-center">
                  <User size={22} className="text-teal-700" />
                </div>
                <div>
                  <p className="font-body text-xs text-[#737373]">Logged in as</p>
                  <p className="font-heading font-bold text-[#0A0A0A]">Ahmed Al Rashid</p>
                  <p className="font-body text-sm text-[#737373]">Facility Admin</p>
                </div>
                <div className="ml-auto px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                  <span className="text-xs font-body font-medium text-emerald-700">Active</span>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-[#FAFAFA]">
                <div className="w-12 h-12 rounded-xl bg-[#0A0A0A]/5 flex items-center justify-center">
                  <Building size={22} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="font-body text-xs text-[#737373]">Organization</p>
                  <p className="font-heading font-bold text-[#0A0A0A]">Al Noor Healthcare Group</p>
                  <p className="font-body text-sm text-[#737373]">ORG-7781</p>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="grid grid-cols-3 gap-3 pt-2"
              >
                <div className="text-center p-3 rounded-lg bg-[#FAFAFA]">
                  <p className="font-heading font-bold text-lg text-[#0A0A0A]">1</p>
                  <p className="font-body text-xs text-[#737373]">Facility</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-[#FAFAFA]">
                  <p className="font-heading font-bold text-lg text-[#0A0A0A]">Admin</p>
                  <p className="font-body text-xs text-[#737373]">Role</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-[#FAFAFA]">
                  <p className="font-heading font-bold text-lg text-[#0A0A0A]">Full</p>
                  <p className="font-body text-xs text-[#737373]">Access</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.button
              data-testid="enter-portal-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full mt-6 py-3.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-teal-600/25 cursor-pointer"
              onClick={onNext}
            >
              Enter Shafafiya Portal <ArrowRight size={16} />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
